import { t } from "@lingui/macro";
import {
	apiEndpoints,
	type DeveloperKeySummary,
	EXTERNAL_SCOPES,
	type ExternalScope,
	firebaseEnvironment,
} from "@repo/common";
import {
	createDeveloperKey,
	listDeveloperKeys,
	revokeDeveloperKey,
} from "@repo/ui";
import { Copy, Eye, EyeOff, KeyRound, Loader2, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { getContentFieldProps } from "../utils/textDirection";
import { CollapsibleSettingsPanel } from "./CollapsibleSettingsPanel";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "./ui/alert-dialog";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const DEFAULT_SCOPES: ExternalScope[] = ["calls:read"];
const MCP_SERVER_URL = `${apiEndpoints[firebaseEnvironment].publicBaseUrl}/mcp`;

export function DeveloperKeysSettingsCard({
	companyId,
	language = "en",
}: {
	companyId: string;
	language?: "en" | "he";
}) {
	const [keys, setKeys] = useState<DeveloperKeySummary[]>([]);
	const [name, setName] = useState("");
	const [scopes, setScopes] = useState<ExternalScope[]>(DEFAULT_SCOPES);
	const [createdSecret, setCreatedSecret] = useState<{
		keyId: string;
		value: string;
	} | null>(null);
	const [showSecret, setShowSecret] = useState(false);
	const [loading, setLoading] = useState(true);
	const [creating, setCreating] = useState(false);
	const [keyToRevoke, setKeyToRevoke] = useState<DeveloperKeySummary | null>(
		null,
	);
	const [revoking, setRevoking] = useState(false);

	const loadKeys = useCallback(async () => {
		setLoading(true);
		try {
			const result = await listDeveloperKeys({ companyId });
			setKeys(result.data.keys);
		} catch {
			toast.error(
				t({
					id: "developerKeysLoadFailed",
					message: "Unable to load API keys.",
				}),
			);
		} finally {
			setLoading(false);
		}
	}, [companyId]);

	useEffect(() => {
		void loadKeys();
	}, [loadKeys]);

	const toggleScope = (scope: ExternalScope) => {
		setScopes((current) =>
			current.includes(scope)
				? current.filter((item) => item !== scope)
				: [...current, scope],
		);
	};

	const handleCreate = async () => {
		if (!name.trim() || scopes.length === 0) return;
		setCreating(true);
		try {
			const result = await createDeveloperKey({
				companyId,
				name: name.trim(),
				scopes,
			});
			setKeys((current) => [...current, result.data.key]);
			setCreatedSecret({
				keyId: result.data.key.id,
				value: result.data.secret,
			});
			setShowSecret(false);
			setName("");
			setScopes(DEFAULT_SCOPES);
			toast.success(
				t({ id: "developerKeyCreated", message: "API key created." }),
			);
		} catch (error) {
			toast.error(
				error instanceof Error
					? error.message
					: t({
							id: "developerKeyCreateFailed",
							message: "Unable to create API key.",
						}),
			);
		} finally {
			setCreating(false);
		}
	};

	const handleRevoke = async () => {
		if (!keyToRevoke) return;
		setRevoking(true);
		try {
			await revokeDeveloperKey({ companyId, credentialId: keyToRevoke.id });
			setKeys((current) => current.filter((key) => key.id !== keyToRevoke.id));
			setCreatedSecret((current) =>
				current?.keyId === keyToRevoke.id ? null : current,
			);
			setKeyToRevoke(null);
			toast.success(
				t({ id: "developerKeyRevoked", message: "API key revoked." }),
			);
		} catch {
			toast.error(
				t({
					id: "developerKeyRevokeFailed",
					message: "Unable to revoke API key.",
				}),
			);
		} finally {
			setRevoking(false);
		}
	};

	return (
		<CollapsibleSettingsPanel
			icon={<KeyRound className="h-5 w-5" />}
			title={t({ id: "developerKeysTitle", message: "Developer API keys" })}
			description={t({
				id: "developerKeysDescription",
				message: "Create up to three non-expiring keys for API and MCP access.",
			})}
		>
			<div className="space-y-5">
				<div className="space-y-2">
					<Label htmlFor="developer-mcp-url">
						{t({ id: "developerMcpUrl", message: "MCP server URL" })}
					</Label>
					<p className="text-sm text-muted-foreground">
						{t({
							id: "developerMcpUrlDescription",
							message:
								"Point your agent to this URL and authenticate with your API key.",
						})}
					</p>
					<div className="flex gap-2">
						<Input
							id="developer-mcp-url"
							readOnly
							value={MCP_SERVER_URL}
							dir="ltr"
						/>
						<Button
							variant="outline"
							size="icon"
							aria-label={t({
								id: "developerMcpUrlCopy",
								message: "Copy MCP server URL",
							})}
							onClick={() => void navigator.clipboard.writeText(MCP_SERVER_URL)}
						>
							<Copy />
						</Button>
					</div>
				</div>

				{createdSecret && (
					<div className="space-y-2 rounded-md border border-warn/30 bg-warn-tint p-4">
						<p className="text-sm font-medium">
							{t({
								id: "developerKeyCopyNow",
								message: "Copy this key now. It will not be shown again.",
							})}
						</p>
						<div className="flex gap-2">
							<Input
								aria-label={t({ id: "developerKeyValue", message: "API key" })}
								readOnly
								type={showSecret ? "text" : "password"}
								value={createdSecret.value}
								dir="ltr"
							/>
							<Button
								variant="outline"
								size="icon"
								aria-label={
									showSecret
										? t({ id: "developerKeyHide", message: "Hide API key" })
										: t({ id: "developerKeyShow", message: "Show API key" })
								}
								onClick={() => setShowSecret((value) => !value)}
							>
								{showSecret ? <EyeOff /> : <Eye />}
							</Button>
							<Button
								variant="outline"
								size="icon"
								aria-label={t({
									id: "developerKeyCopy",
									message: "Copy API key",
								})}
								onClick={() =>
									void navigator.clipboard.writeText(createdSecret.value)
								}
							>
								<Copy />
							</Button>
						</div>
						<Button variant="ghost" onClick={() => setCreatedSecret(null)}>
							{t({ id: "done", message: "Done" })}
						</Button>
					</div>
				)}

				<div className="space-y-3">
					<Label htmlFor="developer-key-name">
						{t({ id: "developerKeyName", message: "Key name" })}
					</Label>
					<Input
						id="developer-key-name"
						value={name}
						onChange={(event) => setName(event.target.value)}
						placeholder={t({
							id: "developerKeyNamePlaceholder",
							message: "Automation or integration name",
						})}
						maxLength={80}
						{...getContentFieldProps(name, language === "he" ? "rtl" : "ltr")}
					/>
					<div>
						<p className="mb-2 text-sm font-medium">
							{t({ id: "developerKeyScopes", message: "Permissions" })}
						</p>
						<Button
							variant="ghost"
							size="sm"
							className="mb-2"
							onClick={() =>
								setScopes(
									scopes.length === EXTERNAL_SCOPES.length
										? []
										: [...EXTERNAL_SCOPES],
								)
							}
						>
							{scopes.length === EXTERNAL_SCOPES.length
								? t({ id: "developerKeyUncheckAll", message: "Uncheck all" })
								: t({ id: "developerKeyCheckAll", message: "Check all" })}
						</Button>
						<div className="grid gap-2 sm:grid-cols-2">
							{EXTERNAL_SCOPES.map((scope) => (
								<label key={scope} className="flex items-center gap-2 text-sm">
									<Checkbox
										checked={scopes.includes(scope)}
										onCheckedChange={() => toggleScope(scope)}
									/>
									{scope}
								</label>
							))}
						</div>
					</div>
					<Button
						onClick={() => void handleCreate()}
						disabled={
							creating ||
							keys.length >= 3 ||
							!name.trim() ||
							scopes.length === 0
						}
					>
						{creating && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
						{t({ id: "developerKeyCreate", message: "Create API key" })}
					</Button>
				</div>

				<div className="space-y-2">
					{loading ? (
						<Loader2 className="h-5 w-5 animate-spin" />
					) : keys.length === 0 ? (
						<p className="text-sm text-muted-foreground">
							{t({ id: "developerKeysEmpty", message: "No active API keys." })}
						</p>
					) : (
						keys.map((key) => (
							<div
								key={key.id}
								className="flex items-start justify-between rounded-md border-[1.5px] border-card-border shadow-card p-3"
							>
								<div>
									<p className="font-medium">{key.name}</p>
									<p className="text-xs text-muted-foreground">
										{key.scopes.join(", ")}
									</p>
								</div>
								<Button
									variant="ghost"
									size="icon"
									aria-label={t({
										id: "developerKeyRevoke",
										message: "Revoke",
									})}
									onClick={() => setKeyToRevoke(key)}
								>
									<Trash2 className="h-4 w-4" />
								</Button>
							</div>
						))
					)}
				</div>
			</div>
			<AlertDialog
				open={Boolean(keyToRevoke)}
				onOpenChange={(open) => !open && setKeyToRevoke(null)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t({ id: "developerKeyRevokeTitle", message: "Revoke API key?" })}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{t({
								id: "developerKeyRevokeDescription",
								message:
									"Write requests using this key will be rejected immediately.",
							})}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={revoking}>
							{t({ id: "cancel", message: "Cancel" })}
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={(event) => {
								event.preventDefault();
								void handleRevoke();
							}}
							disabled={revoking}
						>
							{t({ id: "developerKeyRevoke", message: "Revoke" })}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</CollapsibleSettingsPanel>
	);
}
